import { useMemo } from 'react';

import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { cn } from '#/lib/utils';
import {
  activateMtrUnit,
  fetchMtrUnitList,
} from '#/api/imsMgmt/settings/mtr/units';
import { mtrUnitListQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import CoverOverlay from '#/modules/commonComponents/CoverOverlay';
import EntityStatusBadge from '#/modules/commonComponents/table/EntityStatusBadge';
import {
  getMtrApiErrorMessage,
  isMtrVersionConflict,
} from '#/modules/imsMgmt/settings/mtr/helper/apiError';
import {
  formatMtrUnitStatusLabel,
  resolveMtrUnitCode,
  resolveMtrUnitUpdatedAt,
} from '#/modules/imsMgmt/settings/mtr/helper/mtrUnitHelpers';

const ACTIVATE_CONFIRM = {
  title: '啟用物料單位',
  message: '確定要啟用此單位？啟用後請再按「儲存」，讓換算設定寫回此物料。',
  confirmText: '啟用',
};

const UNIT_ROW_CLASS = cn(
  'form-section__body',
  'grid grid-cols-1 gap-3',
  '@3xl/form-section:grid-cols-3 @3xl/form-section:items-start',
);

/**
 * Create／Edit 同一「物料單位」區塊。
 * 基本單位與一個進位單位同一列；空白進位不送出。子資源僅啟停 INACTIVE。
 */
const MtrUnitsEditor = ({
  mtrId = null,
  canRead = false,
  canUpdate = false,
  control,
  register,
  errors,
  onAfterActivate,
}) => {
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirmDialog();

  const baseUnitName = useWatch({ control, name: 'baseUnitName', defaultValue: '' });
  const baseLabel = String(baseUnitName || '').trim() || '基本單位';

  const hasMtrId = Boolean(mtrId);
  const listEnabled = hasMtrId && canRead;

  const listQuery = useQuery({
    queryKey: mtrUnitListQueryKey(mtrId),
    queryFn: () => fetchMtrUnitList(mtrId),
    enabled: listEnabled,
  });

  const inactiveUnits = useMemo(
    () => (listQuery.data?.data ?? []).filter((row) => row?.status === 'INACTIVE'),
    [listQuery.data?.data],
  );

  const activateMutation = useMutation({
    mutationFn: ({ mtrUnitId, updatedAt }) => activateMtrUnit(mtrId, mtrUnitId, { updatedAt }),
    onSuccess: async (result) => {
      toast.success(result?.message || '物料單位已啟用');
      await queryClient.invalidateQueries({ queryKey: mtrUnitListQueryKey(mtrId) });
      await onAfterActivate?.();
    },
    onError: (error) => {
      toast.error(getMtrApiErrorMessage(error, '啟用物料單位失敗'));
      if (isMtrVersionConflict(error)) {
        queryClient.invalidateQueries({ queryKey: mtrUnitListQueryKey(mtrId) });
      }
    },
  });

  async function handleActivate(unit) {
    const ok = await confirm(ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    activateMutation.mutate({
      mtrUnitId: unit.id,
      updatedAt: resolveMtrUnitUpdatedAt(unit),
    });
  }

  if (hasMtrId && !canRead) {
    return null;
  }

  return (
    <div className="form-section @container/form-section w-full relative">
      {confirmDialog}
      <CoverOverlay
        show={listEnabled && listQuery.isLoading}
        isLoading={listEnabled && listQuery.isLoading}
      />

      <div className="form-section__header">
        <h2 className="form-block__title mr-2">物料單位</h2>
      </div>

      <p className="form-block__description">
        基本單位換算固定為 1。同一列可填一個進位單位（例如 1 捲＝3000 顆）。
        進位欄空白就不會送出。修改後請按儲存才會生效。
      </p>

      {listEnabled && listQuery.isError ? (
        <p className="mt-3 text-destructive text-sm">
          {getMtrApiErrorMessage(listQuery.error, '載入停用單位失敗')}
        </p>
      ) : null}

      <div className={UNIT_ROW_CLASS}>
        <FloatingInput
          id="baseUnitName"
          type="text"
          label="基本單位名稱"
          tip="例如「顆」。已建立的單位只改名稱，不更換 id。"
          register={register}
          errors={errors}
          readOnly={!canUpdate}
        />
        <FloatingInput
          id="unitConvert.0.name"
          type="text"
          label="進位單位名稱"
          tip="例如「捲」"
          register={register}
          errors={errors}
          readOnly={!canUpdate}
        />
        <FloatingInput
          id="unitConvert.0.convertRate"
          type="number"
          label="換算數量"
          tip={`1 個進位單位等於幾個「${baseLabel}」`}
          register={register}
          errors={errors}
          min={0}
          step="any"
          readOnly={!canUpdate}
        />
      </div>

      <input type="hidden" {...register('unitBaseId')} />
      <input type="hidden" {...register('unitConvert.0.id')} />

      {inactiveUnits.length > 0 ? (
        <div className="mt-4 px-3">
          <p className="mb-2 text-muted-foreground text-sm font-medium">
            已停用的單位（啟用後請再儲存物料）
          </p>
          <ul className="m-0 list-none space-y-2 p-0">
            {inactiveUnits.map((unit) => (
              <li
                key={unit.id}
                className={cn(
                  'px-3 py-2',
                  'rounded border border-gray-200',
                  'flex flex-wrap items-center justify-between gap-2',
                )}
              >
                <span className="text-sm inline-flex items-center gap-1.5">
                  <EntityStatusBadge
                    status={unit.status}
                    label={formatMtrUnitStatusLabel(unit.status)}
                  />
                  <span className="font-mono text-xs">{resolveMtrUnitCode(unit)}</span>
                  <span>{unit.name}</span>
                </span>
                {canUpdate ? (
                  <Button
                    type="button"
                    size="sm"
                    disabled={activateMutation.isPending}
                    onClick={() => handleActivate(unit)}
                  >
                    啟用
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

MtrUnitsEditor.propTypes = {
  mtrId: PropTypes.string,
  canRead: PropTypes.bool,
  canUpdate: PropTypes.bool,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  onAfterActivate: PropTypes.func,
};

export default MtrUnitsEditor;
